import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
  TableSortLabel,
  useTheme,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { OnboardingUser } from '../services/HxcStagingService';

type SortableColumn = 'username' | 'fullname' | 'onboardDate' | 'emailSentDate';
type SortDirection = 'asc' | 'desc' | null;

interface OnboardingTableProps {
  users: OnboardingUser[];
  onRefresh: () => void;
  onEditDate: (user: OnboardingUser) => void;
  onDelete: (user: OnboardingUser) => void;
}

const OnboardingTable: React.FC<OnboardingTableProps> = ({
  users,
  onRefresh,
  onEditDate,
  onDelete
}) => {
  const theme = useTheme();
  
  // State for sorting
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Handle sort
  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null (unsorted)
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      // New column: start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return users; // Return unsorted (by id)
    }
    
    return [...users].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      
      // Handle null/undefined values and dates
      if (sortColumn === 'onboardDate' || sortColumn === 'emailSentDate') {
        // Handle dates - null dates go to the end
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        
        const aTime = new Date(aValue).getTime();
        const bTime = new Date(bValue).getTime();
        
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      } else {
        // Handle strings
        if (!aValue) aValue = '';
        if (!bValue) bValue = '';
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (aStr < bStr) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });
  }, [users, sortColumn, sortDirection]);
  
  // Header cell styling
  const headerCellStyle = {
    fontWeight: 600, 
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.025em',
    py: 1.5,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04)
    },
    '& .MuiTableSortLabel-root': {
      color: theme.palette.text.primary,
      '&:hover': {
        color: theme.palette.text.primary,
      },
      '&.Mui-active': {
        color: theme.palette.text.primary,
      }
    }
  };
  
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'username'}
                direction={sortColumn === 'username' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('username')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'username' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Username
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'fullname'}
                direction={sortColumn === 'fullname' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('fullname')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'fullname' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Full Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'onboardDate'}
                direction={sortColumn === 'onboardDate' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('onboardDate')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'onboardDate' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Onboard Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>
              <TableSortLabel
                active={sortColumn === 'emailSentDate'}
                direction={sortColumn === 'emailSentDate' ? sortDirection || 'asc' : 'asc'}
                onClick={() => handleSort('emailSentDate')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: sortColumn === 'emailSentDate' ? `${theme.palette.primary.main} !important` : undefined
                  }
                }}
              >
                Last Email Sent
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyle}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" color="textSecondary">
                  No users in onboarding
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedUsers.map((user) => (
              <TableRow 
                key={user.id} 
                hover
                sx={{ 
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`
                  }
                }}
              >
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.fullname}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                    <span>
                      {user.onboardDate ? new Date(user.onboardDate).toLocaleDateString() : '-'}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => onEditDate(user)}
                      aria-label="edit onboard date"
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  {user.emailSentDate ? new Date(user.emailSentDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(user)}
                    aria-label="delete onboarding entry"
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OnboardingTable;
